import axios from 'axios';
import type { Dispatch } from 'redux';

import { isAuthPath, isUnauthorizedError, recoverFromUnauthorized, withCurrentBearer } from '@/session/reauth';
import { endSession, refreshSession } from '@/session/refresh-session';

import { showToast } from '../../components/reusable/toast';
import {
	COMPANY_ORGANIZATION,
	COREAPI,
	getUserData,
	ISADMINLOGGED,
	ISSESSION,
	ISSUPERADMIN,
	onLogout,
} from '../../constants/global';
import { globalType } from '../types';

export type ReduxDispatch = Dispatch<{ type: string; payload: unknown }>;

export const header = () => ({
	'Content-Type': 'application/json',
	'Authorization': getUserData().token ? `Bearer ${getUserData().token}` : '',
	'UserId': getUserData()?.user?.UserId ?? '',
	'FullName': getUserData()?.user?.FullName ?? '',
	'PartyId': !ISSUPERADMIN ? getUserData().user?.PartyId : String(COMPANY_ORGANIZATION.id ?? ''),
});

export const headerMultipart = () => ({
	Authorization: getUserData().token ? `Bearer ${getUserData().token}` : '',
	UserId: getUserData()?.user?.UserId ?? '',
	FullName: getUserData()?.user?.FullName ?? '',
});

const abortControllers = new Map<string, AbortController>();

const cancelPreviousRequest = (actionType: string): AbortSignal => {
	const existing = abortControllers.get(actionType);
	if (existing) existing.abort();
	const controller = new AbortController();
	abortControllers.set(actionType, controller);
	return controller.signal;
};

const TIMEOUT = 10000 * 30;

type I_API_REQUEST = (
	url: string | undefined,
	headers: Record<string, string | undefined>,
	data: unknown,
	method: 'get' | 'post' | 'put' | 'delete' | 'patch',
	timeout: number | null,
	validateStatus?: (status: number) => boolean,
	dispatch?: ReduxDispatch,
	dataError?: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- callbacks accept typed params from callers (contravariant)
	onFailure?: (err: any) => void,
	dataLoading?: string,
	dataAction?: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- callbacks accept typed params from callers (contravariant)
	onSuccess?: (res: any) => void
) => Promise<void>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pass-through for typed callbacks
const statusFunction = (onStatus?: (res: any) => void, res?: unknown) => {
	if (typeof onStatus === 'function') onStatus(res);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pass-through for typed callbacks
const apiRespond = (dispatch: ReduxDispatch, type: string | undefined, payload: unknown, onStatus?: (res: any) => void) => {
	dispatch({ type: type!, payload });
	statusFunction(onStatus, payload);
};

const apiRequest: I_API_REQUEST = async (
	url,
	headers,
	data,
	method,
	timeout,
	validateStatus,
	dispatch,
	_dataError,
	_onFailure,
	dataLoading,
	dataAction,
	onSuccess
) => {
	const signal = cancelPreviousRequest(dataAction!);

	// These calls use bare `axios`, so they get none of the `api-client` interceptors. The
	// same one-shot 401 recovery is applied by hand: a 401 may arrive either as a resolved
	// response (GETs pass `validateStatus: () => true`) or as a thrown AxiosError.
	const fetchData = async (afterRefresh = false): Promise<void> => {
		const sentToken = getUserData().token;
		const canRecover = !afterRefresh && !isAuthPath(url);
		let res;
		try {
			res = await axios({
				url,
				method,
				data: data ?? {},
				timeout: timeout || 0,
				validateStatus,
				headers: afterRefresh ? withCurrentBearer(headers) : headers,
				signal,
			});
		} catch (error) {
			if (canRecover && isUnauthorizedError(error) && (await recoverFromUnauthorized(sentToken)) === 'replay') {
				return fetchData(true);
			}
			throw error;
		}

		if (res.status === 401 && canRecover && (await recoverFromUnauthorized(sentToken)) === 'replay') {
			return fetchData(true);
		}

		if (res.status >= 200 && res.status < 300) {
			apiRespond(dispatch!, dataAction, res.data, onSuccess);
		} else if (res.data.message) {
			showToast('error', res.data.message);
			// apiRespond(dispatch, globalType.notificationGlobal.dataAction, { notice: res.data.message, status: false }, onFailure);
		} else {
			throw new Error(res.data.message);
		}
	};

	try {
		if (!ISSESSION() && !isAuthPath(url)) {
			if (!ISADMINLOGGED()) {
				onLogout();
				window.location.reload();
			} else {
				// Access token already past `exp`: refresh before the round trip rather than
				// after a guaranteed 401. Only a rejected refresh ends the session; a transient
				// failure lets the request go out and speak for itself.
				const outcome = await refreshSession(getUserData().token);
				if (outcome === 'dead') {
					endSession('expired');
				} else {
					await fetchData(outcome === 'rotated' || outcome === 'adopted');
				}
			}
		} else {
			await fetchData();
		}
	} catch (error: unknown) {
		if (axios.isCancel(error) || (error instanceof DOMException && error.name === 'AbortError')) {
			// Request was canceled — no action needed
		} else {
			// Surface the real backend error instead of a blanket "No response".
			let message = 'Something went wrong. Please try again.';
			let status = 0;
			if (axios.isAxiosError(error)) {
				if (error.response) {
					status = error.response.status;
					const body = error.response.data as { message?: string; title?: string } | undefined;
					message = body?.message || body?.title || `Request failed (${status})`;
				} else {
					message = 'No response from server. Check your connection and try again.';
				}
			}
			showToast('error', message);
			// Record the failure on the action's error slice so views can render a real error state.
			if (dispatch && _dataError) {
				dispatch({ type: _dataError, payload: status ? `${message} (${status})` : message });
			}
		}
	} finally {
		dispatch!({ type: dataLoading!, payload: false });
	}
};

const apiAuthRequest: I_API_REQUEST = async (
	url,
	headers,
	data,
	method,
	timeout,
	validateStatus,
	dispatch,
	_dataError,
	onFailure,
	dataLoading,
	dataAction,
	onSuccess
) => {
	const signal = cancelPreviousRequest(dataAction!);
	try {
		const res = await axios({
			url,
			method,
			data: data || {},
			timeout: timeout || 0,
			validateStatus,
			headers,
			signal,
		});

		if (url?.includes('logout') && res.status === 401) {
			onLogout();
			window.open('/login', '_self');
			return;
		}

		if (res.status >= 200 && res.status < 300) {
			apiRespond(dispatch!, dataAction, res.data, onSuccess);
		} else if (res.data.message) {
			showToast('error', res.data.message);
			apiRespond(dispatch!, globalType.notificationGlobal.dataAction, { notice: res.data.message, status: false }, onFailure);
		} else {
			throw new Error(res.data.message);
		}
	} catch (error: unknown) {
		if (axios.isCancel(error) || (error instanceof DOMException && error.name === 'AbortError')) {
			// Request was canceled — no action needed
		} else {
			// apiRespond(
			// 	dispatch,
			// 	globalType.notificationGlobal.dataAction,
			// 	{
			// 		notice: 'No response',
			// 		status: false,
			// 	},
			// 	onFailure
			// );
			showToast('error', 'No response');
		}
	} finally {
		dispatch!({ type: dataLoading!, payload: false });
	}
};

export const getErrMsg = (error: unknown) => {
	if (error != null) {
		const { data } = (error as { response?: { data?: unknown } }).response || {};
		if (typeof data === 'object' && data !== null) {
			const { message } = data as { message?: string };
			return message;
		}
		return data;
	}
	return '';
};

export interface I_ACTION_TYPE {
	dataLoading: string;
	dataAction: string;
	dataError: string;
}

interface I_HTTPMETHOD {
	apiData: {
		url: string;
		header: Record<string, string | undefined>;
		customurl?: string;
		data?: unknown;
	};
	actionType: I_ACTION_TYPE;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- callbacks accept typed params from callers (contravariant)
	onSuccess?: (res: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- callbacks accept typed params from callers (contravariant)
	onFailure?: (err: any) => void;
	auth?: boolean;
}

export const httpGetMethod = ({
	apiData: { url, header, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			null,
			'get',
			null,
			() => true,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpPostMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		// Clear any prior error for this action so a fresh request starts from a clean error state.
		dispatch({ type: dataError, payload: null });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'post',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpUploadMediaMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		// Clear any prior error for this action so a fresh request starts from a clean error state.
		dispatch({ type: dataError, payload: null });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'post',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpPutVideoMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'put',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpDeleteMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'delete',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const clearHttp = (type: string) => (dispatch: ReduxDispatch) => {
	dispatch({ type: `${type}Loading`, payload: false });
	dispatch({ type, payload: [] });
	dispatch({ type: `${type}Error`, payload: '' });
};

export const clearHttpByValue =
	({
		actionType: { dataLoading, dataAction, dataError },
		dataActionValue,
	}: {
		actionType: I_ACTION_TYPE;
		dataActionValue: unknown;
	}) =>
	(dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: false });
		dispatch({ type: dataAction, payload: dataActionValue });
		dispatch({ type: dataError, payload: '' });
	};

export const httpPutMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		await apiRequest(
			requesturl,
			header,
			data,
			'put',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpPatchMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : `${COREAPI}${url}`;
	return async (dispatch: ReduxDispatch) => {
		dispatch({ type: dataLoading, payload: true });
		await apiRequest(
			requesturl,
			header,
			data,
			'patch',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};
